import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { mediaUrl } from '../api/client';
import {
  useAddCandidate,
  useCreatePoll,
  useDeleteCandidate,
  useDeletePoll,
  usePoll,
  useReorderCandidates,
  useUpdateCandidate,
  useUpdatePoll,
} from '../api/queries';
import type { Candidate } from '../api/types';
import { ConfirmDeletePoll } from '../components/ConfirmDeletePoll';
import { PauseToggle } from './PollsList';
import { Dialog, dialogStyles } from '../components/Dialog';
import { MediaPicker } from '../components/MediaPicker';
import { PoliticalField } from '../components/PoliticalField';
import { TagPicker } from '../components/TagPicker';
import ui from '../components/ui.module.css';
import { errorMessage, isVotesConflict } from '../copy';
import styles from './PollEditor.module.css';

/**
 * Create and edit, one screen (02-requirements.md §Admin Dashboard — five
 * screens, and this is one of them). Which mode it is in is the route: `nova`
 * has no poll behind it yet, so its candidates are a local draft until the poll
 * is created — the API refuses a poll with fewer than two pictured candidates,
 * and a half-made poll must never reach the app.
 *
 * On an existing poll every edit is its own request. Nothing is batched: the
 * API patches fields independently, and a "save everything" button would send
 * fields the administrator never touched.
 */
interface Draft {
  title: string;
  /** Categorias, em ordem: a primeira é a principal (§Poll Categories). */
  tags: string[];
  description: string;
  coverImage: string | null;
  /** Enquete política: as regras eleitorais valem (components/PoliticalField). */
  political: boolean;
}

const EMPTY: Draft = { title: '', tags: [], description: '', coverImage: null, political: false };

/**
 * Título e descrição são obrigatórios (02-requirements.md §Poll Administration).
 * A regra vale nos dois modos da tela: uma enquete não é criada sem eles, e uma
 * enquete existente não pode ficar sem eles — apagar o campo e sair dele não
 * envia PATCH nenhum, porque um título vazio na lista do painel e uma descrição
 * vazia na tela de detalhe do app são exatamente o que a regra existe para
 * impedir.
 */
const REQUIRED = {
  title: { min: 3, message: 'O título é obrigatório e precisa de pelo menos 3 caracteres.' },
  description: { min: 1, message: 'A descrição é obrigatória.' },
} as const;

type RequiredField = keyof typeof REQUIRED;

export function PollEditor(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const creating = id === 'nova';
  const navigate = useNavigate();

  const { data, isLoading, error } = usePoll(creating ? undefined : id);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [newCandidates, setNewCandidates] = useState<{ name: string; imageUrl: string }[]>([]);
  const [picking, setPicking] = useState<null | { target: 'cover' | 'draft' | string }>(null);
  const [confirming, setConfirming] = useState(false);
  /** The candidate the API refused to delete because it holds votes. */
  const [forcing, setForcing] = useState<Candidate | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /** Campos obrigatórios que o administrador deixou vazios, por campo. */
  const [invalid, setInvalid] = useState<Partial<Record<RequiredField, string>>>({});
  const [tagError, setTagError] = useState<string | null>(null);
  const [addName, setAddName] = useState('');
  const [addImage, setAddImage] = useState<string | null>(null);

  const pollId = data?.poll.id ?? '';
  const createPoll = useCreatePoll();
  const updatePoll = useUpdatePoll(pollId);
  const deletePoll = useDeletePoll(pollId);
  const addCandidate = useAddCandidate(pollId);
  const updateCandidate = useUpdateCandidate(pollId);
  const deleteCandidate = useDeleteCandidate(pollId);
  const reorder = useReorderCandidates(pollId);

  useEffect(() => {
    if (!data) return;
    setInvalid({});
    setTagError(null);
    setDraft({
      title: data.poll.title,
      // `tags` é a lista; `category` é a cabeça dela, e serve de fallback para
      // uma enquete que a API ainda não devolveu com tags.
      tags: data.poll.tags?.length
        ? data.poll.tags.map((tag) => tag.name)
        : data.poll.category
          ? [data.poll.category]
          : [],
      description: data.poll.description ?? '',
      coverImage: data.poll.cover_image,
      political: Boolean(data.poll.political),
    });
  }, [data]);

  const candidates: Candidate[] = data?.candidates ?? [];

  const saveField = (patch: Partial<Draft>): void => {
    setFailure(null);
    if (creating) return;
    updatePoll.mutate(
      {
        ...(patch.title !== undefined ? { title: patch.title } : {}),
        ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
        ...(patch.description !== undefined ? { description: patch.description } : {}),
        ...(patch.coverImage !== undefined ? { coverImage: patch.coverImage } : {}),
      },
      { onError: (cause) => setFailure(errorMessage(cause)) },
    );
  };

  /**
   * Grava um campo obrigatório, ou recusa o vazio sem chamar a API. Recusando,
   * o valor mostrado volta ao último que a API confirmou: deixar o campo em
   * branco na tela enquanto o servidor guarda outra coisa faz a tela mentir
   * sobre o que está publicado.
   */
  const saveRequired = (field: RequiredField, raw: string): void => {
    const value = raw.trim();
    if (value.length < REQUIRED[field].min) {
      setInvalid((current) => ({ ...current, [field]: REQUIRED[field].message }));
      if (data) setDraft((current) => ({ ...current, [field]: data.poll[field] ?? '' }));
      return;
    }
    setInvalid((current) => ({ ...current, [field]: undefined }));
    setDraft((current) => ({ ...current, [field]: value }));
    saveField({ [field]: value });
  };

  /**
   * A lista de categorias inteira, a cada mudança. É uma lista, não um campo:
   * mandar só a que entrou deixaria o servidor decidir a ordem, e a ordem é o
   * que define a categoria principal. Uma enquete sem categoria nenhuma é
   * recusada aqui e no schema da API.
   */
  const saveTags = (next: string[]): void => {
    if (next.length === 0) {
      setTagError('A enquete precisa de pelo menos uma categoria.');
      return;
    }
    setTagError(null);
    setDraft((current) => ({ ...current, tags: next }));
    saveField({ tags: next });
  };

  const onCreate = (): void => {
    setFailure(null);
    createPoll.mutate(
      {
        title: draft.title.trim(),
        tags: draft.tags,
        description: draft.description.trim(),
        coverImage: draft.coverImage,
        political: draft.political,
        candidates: newCandidates,
      },
      {
        // A rota responde `{poll, candidates}`, não a enquete crua.
        onSuccess: (created) => void navigate(`/enquetes/${created.poll.id}`, { replace: true }),
        onError: (cause) => setFailure(errorMessage(cause)),
      },
    );
  };

  const move = (index: number, by: -1 | 1): void => {
    const next = [...candidates];
    const target = index + by;
    const moved = next[index];
    const displaced = next[target];
    if (!moved || !displaced) return;
    next[index] = displaced;
    next[target] = moved;
    setFailure(null);
    // The whole list goes up, never a pair: a partial set would leave two
    // candidates on one position and the ranking's last tie-break would be
    // nobody's decision (the API answers 400 candidate_set_mismatch).
    reorder.mutate(
      next.map((candidate) => candidate.id),
      { onError: (cause) => setFailure(errorMessage(cause)) },
    );
  };

  const removeCandidate = (candidate: Candidate): void => {
    setFailure(null);
    deleteCandidate.mutate(
      { id: candidate.id },
      {
        onError: (cause) => {
          // The API refused because votes point at it (409). Ask for the
          // destructive path explicitly and say what it costs — a silent
          // cascade would delete the evidence the leaderboard is derived from.
          if (isVotesConflict(cause)) return setForcing(candidate);
          setFailure(errorMessage(cause));
        },
      },
    );
  };

  const onPick = (path: string): void => {
    if (!picking) return;
    if (picking.target === 'cover') {
      setDraft((current) => ({ ...current, coverImage: path }));
      saveField({ coverImage: path });
    } else if (picking.target === 'draft') {
      setAddImage(path);
    } else {
      updateCandidate.mutate(
        { id: picking.target, patch: { imageUrl: path } },
        { onError: (cause) => setFailure(errorMessage(cause)) },
      );
    }
  };

  if (!creating && isLoading) return <p className={ui.muted}>Carregando…</p>;
  if (!creating && error) return <p className={ui.error}>{errorMessage(error)}</p>;

  // Título, descrição e categoria obrigatórios, e **uma** opção basta — o mesmo
  // mínimo que o schema da API cobra (02-requirements.md §Poll Administration).
  const canCreate =
    draft.title.trim().length >= REQUIRED.title.min &&
    draft.description.trim().length >= REQUIRED.description.min &&
    draft.tags.length >= 1 &&
    newCandidates.length >= 1;

  return (
    <section className={ui.panel}>
      <div className={styles.header}>
        <h1 className={ui.h1}>{creating ? 'Nova enquete' : draft.title}</h1>
        <button type="button" className={ui.ghost} onClick={() => void navigate('/')}>
          Voltar
        </button>
      </div>

      {failure ? <p className={ui.error}>{failure}</p> : null}

      <div className={styles.columns}>
        <div>
          <div className={ui.field}>
            {/* A marca de obrigatório fica FORA do <label>: dentro dele ela
                entraria no nome do campo lido em voz alta ("Título asterisco"). */}
            <span className={styles.labelRow}>
              <label className={ui.label} htmlFor="title">
                Título
              </label>
              <span aria-hidden="true" className={styles.required}>
                *
              </span>
            </span>
            <input
              id="title"
              className={ui.input}
              required
              aria-required="true"
              aria-invalid={invalid.title ? true : undefined}
              aria-describedby={invalid.title ? 'title-error' : undefined}
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              onBlur={(event) => saveRequired('title', event.target.value)}
            />
            {invalid.title ? (
              <p id="title-error" className={ui.error}>
                {invalid.title}
              </p>
            ) : null}
          </div>

          <TagPicker
            value={draft.tags}
            onChange={(next) => (creating ? setDraft({ ...draft, tags: next }) : saveTags(next))}
            error={tagError}
          />

          <div className={ui.field}>
            <span className={styles.labelRow}>
              <label className={ui.label} htmlFor="description">
                Descrição
              </label>
              <span aria-hidden="true" className={styles.required}>
                *
              </span>
            </span>
            <textarea
              id="description"
              className={ui.textarea}
              required
              aria-required="true"
              aria-invalid={invalid.description ? true : undefined}
              aria-describedby={invalid.description ? 'description-error' : undefined}
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              onBlur={(event) => saveRequired('description', event.target.value)}
            />
            {invalid.description ? (
              <p id="description-error" className={ui.error}>
                {invalid.description}
              </p>
            ) : null}
          </div>

          <div className={ui.field}>
            <span className={ui.label}>Imagem de capa</span>
            <div className={styles.cover}>
              {draft.coverImage ? (
                <img className={styles.coverImage} src={mediaUrl(draft.coverImage)} alt="" />
              ) : (
                <div className={styles.coverImage} />
              )}
              <button type="button" className={ui.ghost} onClick={() => setPicking({ target: 'cover' })}>
                Escolher
              </button>
            </div>
          </div>

          <PoliticalField
            checked={draft.political}
            blocked={Boolean(data?.poll.political && data.poll.hidden && !data.poll.paused)}
            disabled={!creating && updatePoll.isPending}
            onChange={(political) => {
              setDraft((current) => ({ ...current, political }));
              if (!creating) {
                setFailure(null);
                updatePoll.mutate({ political }, { onError: (cause) => setFailure(errorMessage(cause)) });
              }
            }}
          />

          {!creating ? (
            <div className={ui.field}>
              <label className={ui.label} htmlFor="status">
                Situação
              </label>
              {/* Closing a poll is an edit, not a delete: no poll closes on its
                  own (§Perpetual Polls), an administrator may close one by hand,
                  and the app already renders closed polls. */}
              <select
                id="status"
                className={ui.select}
                value={data?.poll.status ?? 'active'}
                onChange={(event) =>
                  updatePoll.mutate(
                    { status: event.target.value as 'active' | 'closed' },
                    { onError: (cause) => setFailure(errorMessage(cause)) },
                  )
                }
              >
                <option value="active">Aberta</option>
                <option value="closed">Fechada</option>
              </select>
            </div>
          ) : null}

          {!creating && data ? (
            <div className={ui.field}>
              <span className={ui.label}>Visibilidade no app</span>
              {/* Pausing hides the poll from every app surface without touching
                  its status or votes; resuming restores it as it was. */}
              <div className={ui.row}>
                <span className={ui.muted}>
                  {data.poll.paused ? 'Pausada — oculta no app' : 'Visível no app'}
                </span>
                <PauseToggle poll={data.poll} />
              </div>
            </div>
          ) : null}
        </div>

        <div>
          <h2 className={ui.h2}>Opções</h2>
          <p className={styles.reorderNote}>
            A ordem é o último critério de desempate do ranking: mudá-la pode mudar um pódio já estabelecido.
          </p>

          {creating ? (
            <>
              {newCandidates.map((candidate, index) => (
                <div className={styles.candidate} key={`${candidate.name}-${index}`}>
                  <img className={ui.thumb} src={mediaUrl(candidate.imageUrl)} alt="" />
                  <span>{candidate.name}</span>
                  <button
                    type="button"
                    className={`${ui.ghost} ${ui.small}`}
                    onClick={() =>
                      setNewCandidates(newCandidates.filter((_, position) => position !== index))
                    }
                  >
                    Remover
                  </button>
                </div>
              ))}
              <p className={ui.muted}>
                {newCandidates.length < 1
                  ? 'A enquete precisa de pelo menos uma opção, cada uma com imagem.'
                  : 'Mais de três opções deixam o pódio ser um recorte do campo.'}
              </p>
            </>
          ) : (
            candidates.map((candidate, index) => (
              <div className={styles.candidate} key={candidate.id}>
                <img className={ui.thumb} src={mediaUrl(candidate.image_url)} alt="" />
                <input
                  className={`${ui.input} ${styles.candidateName}`}
                  defaultValue={candidate.name}
                  aria-label={`Nome da opção ${candidate.name}`}
                  onBlur={(event) => {
                    const name = event.target.value.trim();
                    if (!name || name === candidate.name) return;
                    updateCandidate.mutate(
                      { id: candidate.id, patch: { name } },
                      { onError: (cause) => setFailure(errorMessage(cause)) },
                    );
                  }}
                />
                <div className={styles.tools}>
                  <button
                    type="button"
                    className={`${ui.ghost} ${ui.small}`}
                    onClick={() => setPicking({ target: candidate.id })}
                  >
                    Imagem
                  </button>
                  <button
                    type="button"
                    className={`${ui.ghost} ${ui.small}`}
                    disabled={index === 0}
                    aria-label={`Subir ${candidate.name}`}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={`${ui.ghost} ${ui.small}`}
                    disabled={index === candidates.length - 1}
                    aria-label={`Descer ${candidate.name}`}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className={`${ui.danger} ${ui.small}`}
                    aria-label={`Apagar ${candidate.name}`}
                    onClick={() => removeCandidate(candidate)}
                  >
                    Apagar
                  </button>
                </div>
              </div>
            ))
          )}

          <div className={styles.addRow}>
            {addImage ? (
              <img className={ui.thumb} src={mediaUrl(addImage)} alt="" />
            ) : (
              <div className={ui.thumb} />
            )}
            <input
              className={ui.input}
              placeholder="Nome da nova opção"
              aria-label="Nome da nova opção"
              value={addName}
              onChange={(event) => setAddName(event.target.value)}
            />
            <div className={styles.tools}>
              <button
                type="button"
                className={`${ui.ghost} ${ui.small}`}
                onClick={() => setPicking({ target: 'draft' })}
              >
                Imagem
              </button>
              <button
                type="button"
                className={`${ui.primary} ${ui.small}`}
                disabled={!addName.trim() || !addImage}
                onClick={() => {
                  const entry = { name: addName.trim(), imageUrl: addImage as string };
                  const done = (): void => {
                    setAddName('');
                    setAddImage(null);
                  };
                  if (creating) {
                    setNewCandidates([...newCandidates, entry]);
                    done();
                    return;
                  }
                  // Adding late is allowed on a poll voted in for months: the
                  // candidate simply has no history before now, and an unranked
                  // hour is not a zero.
                  addCandidate.mutate(entry, {
                    onSuccess: done,
                    onError: (cause) => setFailure(errorMessage(cause)),
                  });
                }}
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      </div>

      {creating ? (
        <div className={styles.danger}>
          <button type="button" className={ui.primary} disabled={!canCreate} onClick={onCreate}>
            {createPoll.isPending ? 'Criando…' : 'Criar enquete'}
          </button>
        </div>
      ) : (
        <div className={styles.danger}>
          <button type="button" className={ui.danger} onClick={() => setConfirming(true)}>
            Apagar enquete
          </button>
        </div>
      )}

      {picking ? (
        <MediaPicker value={draft.coverImage} onPick={onPick} onClose={() => setPicking(null)} />
      ) : null}

      {forcing ? (
        <Dialog title="Esta opção já recebeu votos" onClose={() => setForcing(null)}>
          <p className={ui.muted}>
            Apagar <strong>{forcing.name}</strong> remove também os votos dados nela e as linhas do histórico
            que sustentam o ranking. O pódio será recalculado sem ela. Não há como desfazer.
          </p>
          <div className={dialogStyles.actions}>
            <button type="button" className={ui.ghost} onClick={() => setForcing(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className={ui.danger}
              onClick={() => {
                const target = forcing;
                setForcing(null);
                deleteCandidate.mutate(
                  { id: target.id, force: true },
                  { onError: (cause) => setFailure(errorMessage(cause)) },
                );
              }}
            >
              Apagar mesmo assim
            </button>
          </div>
        </Dialog>
      ) : null}

      {confirming && data ? (
        <ConfirmDeletePoll
          title={data.poll.title}
          pending={deletePoll.isPending}
          error={deletePoll.error ? errorMessage(deletePoll.error) : null}
          onClose={() => setConfirming(false)}
          onConfirm={(typed) =>
            deletePoll.mutate(typed, {
              onSuccess: () => void navigate('/', { replace: true }),
            })
          }
        />
      ) : null}
    </section>
  );
}
